import { DatePipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import type { NotificationDto } from '@core/api/miportal-api.types';
import { LocaleService } from '@core/services/locale.service';
import { LoggerService } from '@core/services/logger.service.js';
import { AuthStore } from '@core/store/core/auth.store';
import { NotificationsStore, SeverityTab } from '@core/store/core/notifications.store';
import { LucideBell, LucideCheck, LucideInbox } from '@lucide/angular';
import { MmscanButton } from '@mmscan/angular-ui/button';
import { MMSCAN_DIALOG_PARTS, MmscanDialog } from '@mmscan/angular-ui/dialog';
import { MmscanIcon } from '@mmscan/angular-ui/icon';
import { MmscanSkeleton } from '@mmscan/angular-ui/skeleton';
import { TranslatePipe } from '@ngx-translate/core';

interface SeverityConfig {
  /** Left accent stripe on the row, for at-a-glance scanning */
  stripeClass: string;
  /** Colour for the severity label in the meta line */
  textClass: string;
}

@Component({
  selector: 'mp-notification',
  imports: [
    MmscanButton,
    MmscanDialog,
    ...MMSCAN_DIALOG_PARTS,
    MmscanIcon,
    MmscanSkeleton,
    DatePipe,
    NgClass,
    TranslatePipe,
  ],
  templateUrl: './notification.component.html',
  styleUrl: './notification.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationComponent {
  visible = signal(false);

  private authStore = inject(AuthStore);
  private notificationsStore = inject(NotificationsStore);
  private chRef = inject(ChangeDetectorRef);
  private logger = inject(LoggerService);
  private router = inject(Router);

  protected readonly icons = { bell: LucideBell, check: LucideCheck, inbox: LucideInbox };
  protected localeId = inject(LocaleService).localeId;

  // Expose store signals to template
  notifications = this.notificationsStore.notifications;
  unreadNotificationsCount = this.notificationsStore.unreadNotificationsCount;
  notificationsLoading = this.notificationsStore.notificationsLoading;
  hasMoreNotifications = this.notificationsStore.hasMoreNotifications;
  activeSeverityTab = this.notificationsStore.activeSeverityTab;
  highUnreadCount = this.notificationsStore.highUnreadCount;
  mediumUnreadCount = this.notificationsStore.mediumUnreadCount;
  lowUnreadCount = this.notificationsStore.lowUnreadCount;

  // Local state
  expandedNotificationIds = signal<Set<string>>(new Set());

  // Severity tabs
  severityTabs: SeverityTab[] = ['all', 'high', 'medium', 'low'];

  onNotificationClick(): void {
    this.visible.set(true);
    this.logger.info('Notification drawer opened, loading notifications');
    this.notificationsStore.loadNotificationsOnClick();
  }

  onCardClick(notification: NotificationDto): void {
    // Mark notification as read if it's not already read
    if (!notification.readAt) {
      this.notificationsStore.markNotificationAsRead(notification);
      this.chRef.detectChanges();
    }

    // Navigate to the linked resource (if any) and close the panel
    if (notification.resourceType && notification.resourceId) {
      this.navigateByResource(notification.resourceType, notification.resourceId);
      this.visible.set(false);
    }
  }

  private navigateByResource(resourceType: string, resourceId: string): void {
    const commands = this.resolveResourceCommands(resourceType, resourceId);

    if (!commands) {
      this.logger.warn(`No navigation mapping for resourceType "${resourceType}"`);
      return;
    }

    this.router.navigate(commands);
  }

  private resolveResourceCommands(resourceType: string, resourceId: string): unknown[] | undefined {
    switch (resourceType) {
      case 'micare-case':
        return ['/micare/case', resourceId];
      default:
        return undefined;
    }
  }

  loadMore(): void {
    this.notificationsStore.loadMoreNotifications();
  }

  getNotificationText(notification: NotificationDto): string {
    return this.notificationsStore.getNotificationText(notification, this.authStore.userLocale());
  }

  getNotificationTitle(notification: NotificationDto): string {
    return this.notificationsStore.getNotificationTitle(notification, this.authStore.userLocale());
  }

  getSeverityConfig(severity: string): SeverityConfig {
    const configs: Record<string, SeverityConfig> = {
      high: {
        stripeClass: 'notification-row--high',
        textClass: 'notification-severity--high',
      },
      medium: {
        stripeClass: 'notification-row--medium',
        textClass: 'notification-severity--medium',
      },
      low: {
        stripeClass: 'notification-row--low',
        textClass: 'notification-severity--low',
      },
    };
    return configs[severity.toLowerCase()] || configs['low'];
  }

  getResourceName(resourceType: string | undefined): string | undefined {
    if (!resourceType) {
      return undefined;
    }

    switch (resourceType) {
      case 'micare-case':
        return 'MiCare';
      default:
        return undefined;
    }
  }

  toggleExpanded(notificationId: string, event: Event): void {
    event.stopPropagation();
    this.expandedNotificationIds.update((ids) => {
      const newSet = new Set(ids);
      if (newSet.has(notificationId)) {
        newSet.delete(notificationId);
      } else {
        newSet.add(notificationId);
      }
      return newSet;
    });
  }

  isExpanded(notificationId: string): boolean {
    return this.expandedNotificationIds().has(notificationId);
  }

  getTruncatedMessage(notification: NotificationDto): string {
    const fullMessage = this.getNotificationText(notification);
    const maxLength = 50;

    if (fullMessage.length <= maxLength) {
      return fullMessage;
    }

    return fullMessage.substring(0, maxLength) + '...';
  }

  markAsReadExplicit(notification: NotificationDto, event: Event): void {
    event.stopPropagation();
    this.notificationsStore.markNotificationAsRead(notification);
    this.chRef.detectChanges();
  }

  setActiveSeverity(severity: SeverityTab): void {
    this.notificationsStore.setActiveSeverity(severity);
  }

  markAllAsRead(): void {
    this.notificationsStore.markAllAsRead();
  }

  getSeverityCount(severity: SeverityTab): string | undefined {
    switch (severity) {
      case 'high':
        return this.highUnreadCount().toString();
      case 'medium':
        return this.mediumUnreadCount().toString();
      case 'low':
        return this.lowUnreadCount().toString();
      default:
        return undefined;
    }
  }
}
